
import { defineComponent } from '@nuxtjs/composition-api';
import type { Route } from 'vue-router';
import WarRoomCommentPlaceholder from '@/components/partials/war-room/WarRoomComment/WarRoomCommentPlaceholder.vue';
import { isEmpty } from '@/components/utils/core/object';
import Dropdown from '@/components/common/Dropdown/Dropdown.vue';
import DropdownItem from '@/components/common/Dropdown/DropdownItem.vue';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu.vue';
import DropdownToggler from '@/components/common/Dropdown/DropdownToggler.vue';
import ChevronDownRoundedIcon from '@/assets/svg/chevron-down-rounded.vue.svg';
import BravadoInfiniteScrollLoader from '~/components/common/BravadoInfiniteScroll/BravadoInfiniteScrollLoader.vue';
import BravadoInfiniteScrollOffset from '~/components/common/BravadoInfiniteScroll/BravadoInfiniteScrollOffset.vue';
import type { WarRoom } from '~/types/store/warRoom';
import { pluralize } from '~/plugins/filters';
import WarRoomComments from '@/components/partials/war-room/WarRoomComments/WarRoomComments.vue';

type CommentsSortBy = 'Best' | 'Newest first' | 'Newest last';

export default defineComponent({
  name: 'PostCommentsPage',
  components: {
    WarRoomComments,
    WarRoomCommentPlaceholder,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggler,
    ChevronDownRoundedIcon,
    BravadoInfiniteScrollOffset,
    BravadoInfiniteScrollLoader,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    commentTree(): WarRoom.State['commentTree'] {
      return this.$store.state.warRoom.commentTree;
    },

    commentsPagination(): WarRoom.State['commentsPagination'] {
      return this.$store.getters['warRoom/commentsPagination'];
    },
    currentPost(): WarRoom.CurrentPost {
      return this.$store.getters['warRoom/currentPost'];
    },
    commentsCountText(): string {
      const total = this.currentPost.comment_count.total;
      return total ? `${total} ${pluralize(total, 'comment')}` : 'No comments';
    },

    sortBy(): CommentsSortBy {
      if (this.$route.query.sort_comments === 'new') return 'Newest first';
      if (this.$route.query.sort_comments === 'old') return 'Newest last';
      return 'Best';
    },
  },
  watch: {
    '$route.query.sort_comments': {
      handler() {
        this.fetchComments();
      },
    },
    '$route.query.comment': {
      immediate: true,
      handler(newValue: string) {
        if (newValue === 'true') {
          this.fetchComments();
        }
      },
    },
  },
  mounted() {
    if (this.$route.query.comment === 'true') {
      this.scrollToComments();
    }
  },
  methods: {
    fetchPostComments(args: {
      query: Route['query'];
      postSlug: string;
      page: number;
      commentsMutation?: string;
    }): Promise<void> {
      return this.$store.dispatch('warRoom/fetchPostComments', args);
    },
    fetchComments() {
      this.loading = true;

      return this.fetchPostComments({
        query: this.$route.query,
        postSlug: this.$route.params.postSlug,
        page: 1,
        commentsMutation: 'setCommentTree',
      })
        .catch(this.$utils.nuxtErrorPage)
        .finally(() => (this.loading = false));
    },

    async onCommentsLoad(page: number): Promise<void> {
      await this.fetchPostComments({
        query: this.$route.query,
        postSlug: this.$route.params.postSlug,
        page,
      });
    },
    async bestSortingClick() {
      this.logSortingClickEvent('best');

      if (!isEmpty(this.$route.query)) {
        await this.$router.replace({ query: {} });
      }
    },

    commentsSortingClick() {
      this.$analytics.logEvent('war-room__post__comments-sorting-toggle__click');

      if (!this.$auth.loggedIn) {
        this.$router.push(`?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`);
      }
    },

    logSortingClickEvent(sorting: string) {
      this.$analytics.logEvent('war-room__post__comments-sorting-item__click', { sorting });
    },

    /**
     * @todo replace this with more simple approach.
     * Use `#id` to scroll or directly move to that part of the document.
     * ex: `/war-room/posts/post#commentId`
     */
    scrollToComments() {},
  },
});
